import {
  PRODUCT_HOLDING_STATUS_LABELS,
  formatDateTime,
  type MarketImportAnalyzeDto,
  type MarketImportHistoryDto,
  type MarketImportResultDto,
} from '@nova/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, FileSpreadsheet, ScanSearch, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { Button } from '../../components/Button.js';
import { EmptyState } from '../../components/EmptyState.js';
import { GlassPanel, PanelHeader } from '../../components/GlassPanel.js';
import { api, ApiError } from '../../lib/api.js';
import { cn } from '../../lib/cn.js';
import { Field, SelectInput } from './marketWidgets.js';
import { useMarketFacets } from './useMarket.js';

export function MarketImport() {
  const qc = useQueryClient();
  const facets = useMarketFacets();
  const [file, setFile] = useState<File | null>(null);
  const [analysis, setAnalysis] = useState<MarketImportAnalyzeDto | null>(null);
  const [columnMap, setColumnMap] = useState<Record<string, string>>({});
  const [productId, setProductId] = useState('');
  const [marketId, setMarketId] = useState('');
  const [dryRun, setDryRun] = useState<MarketImportResultDto | null>(null);
  const [result, setResult] = useState<MarketImportResultDto | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const history = useQuery({
    queryKey: ['market-import-history'],
    queryFn: () => api.get<MarketImportHistoryDto[]>('/sales/market/import/history'),
  });

  const reset = () => {
    setFile(null);
    setAnalysis(null);
    setColumnMap({});
    setDryRun(null);
    setResult(null);
    if (fileInput.current) fileInput.current.value = '';
  };

  const analyze = useMutation({
    mutationFn: async (chosen: File) => {
      const form = new FormData();
      form.append('file', chosen);
      return api.postForm<MarketImportAnalyzeDto>('/sales/market/import/analyze', form);
    },
    onSuccess: (data) => {
      setAnalysis(data);
      setColumnMap(data.guessedMap);
      setDryRun(null);
      setResult(null);
      // Default to the first product (Aim CMS).
      if (!productId && data.products[0]) setProductId(data.products[0].id);
    },
  });

  const runImport = useMutation({
    mutationFn: async (isDryRun: boolean) => {
      if (!file) throw new ApiError(0, 'Ingen fil valgt.');
      const form = new FormData();
      form.append('columnMap', JSON.stringify(columnMap));
      form.append('productId', productId);
      if (marketId) form.append('marketId', marketId);
      form.append('dryRun', String(isDryRun));
      form.append('file', file);
      return api.postForm<MarketImportResultDto>('/sales/market/import/cms', form);
    },
    onSuccess: (data) => {
      if (data.dryRun) {
        setDryRun(data);
        setResult(null);
      } else {
        setResult(data);
        setDryRun(null);
        void qc.invalidateQueries({ queryKey: ['organizations'] });
        void qc.invalidateQueries({ queryKey: ['market-facets'] });
        void history.refetch();
      }
    },
  });

  const onPickFile = (f: File | null) => {
    setFile(f);
    setAnalysis(null);
    setDryRun(null);
    setResult(null);
    if (f) analyze.mutate(f);
  };

  const requiredUnmapped =
    analysis?.fields.filter((f) => f.required && !columnMap[f.field]).map((f) => f.label) ?? [];
  const canRun = !!file && !!analysis && requiredUnmapped.length === 0 && !!productId;

  return (
    <div className="space-y-5">
      <GlassPanel>
        <PanelHeader
          title="Importer CMS-kartlegging"
          subtitle="Last opp «Kommuner_CMS_kartlegging v2» (CSV/Excel). Importen matcher på kommunenummer og oppdaterer organisasjon + produktbeholdning. Den er idempotent — samme fil to ganger gir ingen duplikater."
          icon={<Upload size={16} />}
        />

        <div className="flex flex-wrap items-end gap-3">
          <Field label="Importer mot produkt" hint="CMS-kartleggingen gjelder Aim CMS.">
            <SelectInput value={productId} onChange={(e) => setProductId(e.target.value)} className="min-w-[14rem]">
              <option value="">Velg produkt …</option>
              {(analysis?.products ?? facets.data?.products ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </SelectInput>
          </Field>
          <Field label="Marked (valgfritt)" hint="Knytt kommunene til et marked.">
            <SelectInput value={marketId} onChange={(e) => setMarketId(e.target.value)} className="min-w-[14rem]">
              <option value="">— ingen —</option>
              {(facets.data?.markets ?? []).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </SelectInput>
          </Field>
          <label className="border-hairline bg-raised/60 hover:bg-raised text-ink inline-flex h-9 cursor-pointer items-center gap-2 rounded-lg border px-3 text-sm transition">
            <FileSpreadsheet size={15} aria-hidden />
            {file ? file.name : 'Velg fil …'}
            <input
              ref={fileInput}
              type="file"
              accept=".csv,.xlsx,text/csv"
              className="hidden"
              onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
            />
          </label>
          {file && (
            <Button size="sm" variant="ghost" onClick={reset}>
              Nullstill
            </Button>
          )}
        </div>

        {analyze.isPending && <p className="text-ink-soft mt-3 text-sm">Leser filen …</p>}
        {analyze.isError && <p className="text-danger mt-3 text-sm">{(analyze.error as ApiError).message}</p>}

        {analysis && (
          <div className="mt-5">
            <h3 className="text-ink text-sm font-medium">Koble kolonner</h3>
            <p className="text-ink-soft/80 mt-0.5 text-xs">
              {analysis.kind.toUpperCase()} · {analysis.rowCount} rader. Nova har foreslått en kobling — juster ved behov.
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {analysis.fields.map((f) => {
                const mapped = columnMap[f.field] ?? '';
                return (
                  <Field
                    key={f.field}
                    label={`${f.label}${f.required ? ' *' : ''}`}
                    hint={mapped ? sampleHint(analysis.sample[mapped]) : undefined}
                  >
                    <SelectInput
                      value={mapped}
                      onChange={(e) => setColumnMap({ ...columnMap, [f.field]: e.target.value })}
                      className={cn(f.required && !columnMap[f.field] && 'border-danger/50')}
                    >
                      <option value="">— ikke koblet —</option>
                      {analysis.headers.map((h) => (
                        <option key={h} value={h}>
                          {h}
                        </option>
                      ))}
                    </SelectInput>
                  </Field>
                );
              })}
            </div>

            {requiredUnmapped.length > 0 && (
              <p className="text-warning mt-3 text-xs">Påkrevde felter mangler kobling: {requiredUnmapped.join(', ')}.</p>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Button
                variant="secondary"
                icon={<ScanSearch size={14} />}
                disabled={!canRun}
                loading={runImport.isPending && runImport.variables === true}
                onClick={() => runImport.mutate(true)}
              >
                Forhåndsvis (tørrkjøring)
              </Button>
              <Button
                variant="primary"
                icon={<Upload size={14} />}
                disabled={!canRun}
                loading={runImport.isPending && runImport.variables === false}
                onClick={() => runImport.mutate(false)}
              >
                Importer
              </Button>
              {runImport.isError && <span className="text-danger text-sm">{(runImport.error as ApiError).message}</span>}
            </div>
          </div>
        )}

        {dryRun && <ImportSummary result={dryRun} title="Forhåndsvisning — ingenting er lagret ennå" preview />}
        {result && <ImportSummary result={result} title="Import fullført" onReset={reset} />}
      </GlassPanel>

      <GlassPanel padded={false}>
        <div className="px-5 pt-5">
          <PanelHeader title="Siste importer" />
        </div>
        {(history.data ?? []).length === 0 ? (
          <div className="pb-4">
            <EmptyState title="Ingen importer ennå." />
          </div>
        ) : (
          <div className="overflow-x-auto px-5 pb-5">
            <table className="w-full min-w-[560px] text-sm">
              <thead>
                <tr className="border-hairline text-ink-soft border-b text-left text-xs">
                  <th className="py-2 pr-3 font-medium">Tidspunkt</th>
                  <th className="py-2 pr-3 font-medium">Fil</th>
                  <th className="py-2 pr-3 font-medium">Resultat</th>
                  <th className="py-2 pr-3 font-medium">Av</th>
                </tr>
              </thead>
              <tbody>
                {(history.data ?? []).map((h) => (
                  <tr key={h.id} className="border-hairline/50 border-b last:border-0">
                    <td className="text-ink-soft py-2 pr-3">{formatDateTime(h.importedAt)}</td>
                    <td className="text-ink-soft py-2 pr-3">{h.fileName ?? '—'}</td>
                    <td className="text-ink-soft py-2 pr-3">
                      {h.rowsCreated} nye · {h.rowsUpdated} oppdatert · {h.rowsSkipped} hoppet over
                    </td>
                    <td className="text-ink-soft py-2 pr-3">{h.importedByName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </GlassPanel>
    </div>
  );
}

function ImportSummary({
  result,
  title,
  preview,
  onReset,
}: {
  result: MarketImportResultDto;
  title: string;
  preview?: boolean;
  onReset?: () => void;
}) {
  return (
    <div className={cn('mt-5 rounded-lg border p-4', result.dryRun ? 'border-hairline bg-accent/[0.04]' : 'border-hairline bg-success/[0.05]')}>
      <div className="flex items-center gap-2">
        {result.dryRun ? (
          <ScanSearch size={16} className="text-accent" aria-hidden />
        ) : (
          <CheckCircle2 size={16} className="text-success" aria-hidden />
        )}
        <p className="text-ink text-sm font-medium">{title}</p>
      </div>
      <div className="text-ink-soft mt-2 flex flex-wrap gap-4 text-sm">
        <span>
          <span className="text-ink font-display text-base">{result.created}</span> nye
        </span>
        <span>
          <span className="text-ink font-display text-base">{result.updated}</span> oppdatert
        </span>
        <span>
          <span className="text-ink font-display text-base">{result.skipped}</span> hoppet over
        </span>
        <span>
          av <span className="text-ink font-display text-base">{result.rowsTotal}</span> rader
        </span>
      </div>
      {result.skippedReasons.length > 0 && (
        <p className="text-warning/90 mt-1 text-[11px]">Hoppet over: {result.skippedReasons.join('; ')}</p>
      )}

      {preview && result.preview.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[420px] text-xs">
            <thead>
              <tr className="border-hairline text-ink-soft border-b text-left">
                <th className="py-1.5 pr-3 font-medium">Knr</th>
                <th className="py-1.5 pr-3 font-medium">Kommune</th>
                <th className="py-1.5 pr-3 font-medium">Status</th>
                <th className="py-1.5 pr-3 font-medium">Dagens løsning</th>
                <th className="py-1.5 pr-3 font-medium">Handling</th>
              </tr>
            </thead>
            <tbody>
              {result.preview.map((p) => (
                <tr key={p.kommunenummer} className="border-hairline/40 border-b last:border-0">
                  <td className="text-ink-soft py-1.5 pr-3 tabular-nums">{p.kommunenummer}</td>
                  <td className="text-ink py-1.5 pr-3">{p.name}</td>
                  <td className="text-ink-soft py-1.5 pr-3">{PRODUCT_HOLDING_STATUS_LABELS[p.status]}</td>
                  <td className="text-ink-soft py-1.5 pr-3">{p.competitor ?? '—'}</td>
                  <td className="text-ink-soft py-1.5 pr-3">{p.action === 'create' ? 'ny' : 'oppdater'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {result.rowsTotal > result.preview.length && (
            <p className="text-ink-soft/60 mt-1 text-[11px]">Viser de første {result.preview.length} radene.</p>
          )}
        </div>
      )}

      {onReset && (
        <Button size="sm" variant="secondary" className="mt-3" onClick={onReset}>
          Ny import
        </Button>
      )}
    </div>
  );
}

function sampleHint(values: string[] | undefined): string | undefined {
  if (!values || values.length === 0) return undefined;
  return `f.eks. ${values.slice(0, 2).join(', ')}`;
}
